import { p as proxyCustomElement, H, h, a as Host } from './index.js';

//#region src/module/util/error/index.ts
const STRIP = "AbortError: ";
var AbortError = class extends Error {
	name = "AbortError";
	constructor(signal) {
		let reason = "Unknown Error";
		if (signal instanceof AbortSignal) {
			reason = String(signal.reason);
			reason = reason.startsWith(STRIP) ? reason.slice(12) : reason;
		}
		super(reason);
	}
};

//#region src/module/util/promise/index.ts
const PromiseUtil = {
	coalesce(fn) {
		let promise;
		return ((...args) => {
			if (promise) return promise;
			promise = fn(...args).finally(() => {
				promise = void 0;
			});
			return promise;
		});
	},
	once(fn) {
		let promise;
		return ((...args) => {
			if (promise) return promise;
			promise = fn(...args);
			return promise;
		});
	},
	extract() {
		let resolve;
		let reject;
		const promise = new Promise((res, rej) => {
			resolve = res;
			reject = rej;
		});
		return {
			promise,
			resolve: (res) => resolve(res),
			reject: (err) => reject(err)
		};
	},
	latest(fn) {
		let abortControllers = [];
		return (async (...args) => {
			for (const abortController of abortControllers) abortController.abort();
			const ctrl = new AbortController();
			abortControllers.push(ctrl);
			try {
				const ret = await fn(...args);
				if (ctrl.signal.aborted) throw new AbortError(ctrl.signal);
				return ret;
			} finally {
				abortControllers = abortControllers.filter((c) => c !== ctrl);
			}
		});
	}
};

// Icon names should always be in kebab-case
// Icons should always be lazy loaded, meaning a function that returns a Promise with the SVG markup.
// Generally speaking we only use outlined (and not filled) icons.
// In case a filled icon is used, omit the `-filled` suffix from the icon name.
// We don't combine filled and outlined icons, so we don't need developers to specify this.
// The design team will decide when a filled icon is used instead of an outlined one.
const ICONS = {
    ad: [async () => await import('./p-DPrd25xM.js'), []],
    adjustments: [async () => await import('./p-lWrDoaL3.js'), []],
    'alert-circle': [async () => await import('./p-BDIVI97X.js'), []],
    'alert-circle-filled': [async () => await import('./p-CK7cizZQ.js'), []],
    'alert-triangle': [async () => await import('./p-DUUW48LH.js'), []],
    'alert-triangle-filled': [async () => await import('./p-DhK_g8Uc.js'), []],
    'align-box-bottom-center': [async () => await import('./p-BzhYgajg.js'), []],
    'align-box-center-middle': [async () => await import('./p-ZH8b7msg.js'), []],
    'align-box-left-middle': [async () => await import('./p-CgdajYvE.js'), []],
    'align-box-right-middle': [async () => await import('./p-RortRP0l.js'), []],
    'align-box-top-center': [async () => await import('./p-Wn_zRleB.js'), []],
    'align-justified': [async () => await import('./p-CbBNLNh1.js'), []],
    'antenna-bars-5': [async () => await import('./p-RLY0WlMu.js'), []],
    apps: [async () => await import('./p-BDNvJpXv.js'), []],
    'arrow-back-up': [async () => await import('./p-BquBxZu3.js'), []],
    'arrow-bar-both-horizontal': [async () => await import('./p-CnEoSORv.js'), []],
    'arrow-bar-both-vertical': [async () => await import('./p-CnEoSORv.js'), ['rotate-90']],
    'arrow-bar-down': [async () => await import('./p-DZ8_VJZL.js'), []],
    'arrow-bar-to-left': [async () => await import('./p-BTRJfpXu.js'), []],
    'arrow-bar-to-right': [async () => await import('./p-BDUdqUh1.js'), []],
    'arrow-bar-up': [async () => await import('./p-DqrKoIRF.js'), []],
    'arrow-down': [async () => await import('./p-Dg7HwegZ.js'), []],
    'arrow-fork': [async () => await import('./p-Ckn0HKgP.js'), []],
    'arrow-fork-rotated': [async () => await import('./p-Ckn0HKgP.js'), ['rotate-180']],
    'arrow-left': [async () => await import('./p-BulLqURs.js'), []],
    'arrow-ramp-left': [async () => await import('./p-BhpZLUlS.js'), []],
    'arrow-right': [async () => await import('./p-j6MhDY7-.js'), []],
    'arrow-up': [async () => await import('./p-Dti39P2n.js'), []],
    'arrow-up-right': [async () => await import('./p-ChrrUbM3.js'), []],
    'arrows-diagonal-minimize': [async () => await import('./p-C2Z2_-nG.js'), []],
    'arrows-diagonal': [async () => await import('./p-AkYZ8M1x.js'), []],
    'arrows-split': [async () => await import('./p-D_IO-Gve.js'), []],
    'arrow-merge': [async () => await import('./p-4BnBFqHO.js'), []],
    'aspect-ratio': [async () => await import('./p-CkaK9L9G.js'), []],
    'badge-cc': [async () => await import('./p-CuFfP1Js.js'), []],
    ban: [async () => await import('./p-BIAfRQ51.js'), []],
    'baseline-density-large': [async () => await import('./p-DUa5seMB.js'), []],
    'baseline-density-medium': [async () => await import('./p-CQJCZJct.js'), []],
    'baseline-density-small': [async () => await import('./p-DZDDDfif.js'), []],
    bolt: [async () => await import('./p-DejQHg9K.js'), []],
    'brand-facebook': [async () => await import('./p-BJIsDfA9.js'), []],
    'brand-google': [async () => await import('./p-CEwK7HSq.js'), []],
    'brand-linkedin': [async () => await import('./p-CDJX2gqj.js'), []],
    'brand-pinterest': [async () => await import('./p-jQQtPyNW.js'), []],
    'brand-reddit': [async () => await import('./p-C5nyB40z.js'), []],
    'brand-tumblr': [async () => await import('./p-DHWqgWko.js'), []],
    'brand-x': [async () => await import('./p-CjymYwuu.js'), []],
    bulb: [async () => await import('./p-DwE4HNmI.js'), []],
    calendar: [async () => await import('./p-DmhSPrPo.js'), []],
    cast: [async () => await import('./p-KHpP4Hjd.js'), []],
    chalkboard: [async () => await import('./p-DH7WRpDz.js'), []],
    'chart-bar': [async () => await import('./p-BCP5nVzD.js'), []],
    'chart-histogram': [async () => await import('./p-kWGp2lF2.js'), []],
    check: [async () => await import('./p-CpsxcZjP.js'), []],
    checks: [async () => await import('./p-CZo6i-Y-.js'), []],
    'chevron-down': [async () => await import('./p-BYiFLu1A.js'), []],
    'chevron-left': [async () => await import('./p-f_Xod3PV.js'), []],
    'chevron-right': [async () => await import('./p-BmoiXug8.js'), []],
    'chevron-up': [async () => await import('./p-g93MALyy.js'), []],
    click: [async () => await import('./p-C6CfdOkL.js'), []],
    'circle-check': [async () => await import('./p-3qHx4mKQ.js'), []],
    'circle-percentage': [async () => await import('./p-D_VRYbqc.js'), []],
    'clipboard-list': [async () => await import('./p-BZFzudKx.js'), []],
    'clock-check': [async () => await import('./p-Bty1eC1G.js'), []],
    'clock-play': [async () => await import('./p-CY6WQMLy.js'), []],
    'clipboard-data': [async () => await import('./p-W1t6tmXJ.js'), []],
    clock: [async () => await import('./p-D8JTtCnF.js'), []],
    code: [async () => await import('./p-DwZItNki.js'), []],
    columns: [async () => await import('./p-Fq_BnY0e.js'), []],
    components: [async () => await import('./p-DD_D7dck.js'), []],
    copy: [async () => await import('./p-Dz-90q6R.js'), []],
    coin: [async () => await import('./p-DaQB6qm1.js'), []],
    'credit-card': [async () => await import('./p-CvD6ckRx.js'), []],
    'currency-dollar': [async () => await import('./p-CO-U_4Eq.js'), []],
    cut: [async () => await import('./p-7OOQT0I4.js'), []],
    'device-desktop': [async () => await import('./p-CLdM01kl.js'), []],
    'device-mobile-rotated': [async () => await import('./p-BKERuQdk.js'), []],
    'device-mobile': [async () => await import('./p-D43Ubvq9.js'), []],
    'device-mobile-up': [async () => await import('./p-CAa9XVbW.js'), []],
    devices: [async () => await import('./p-CaxWIWwm.js'), []],
    'devices-pin': [async () => await import('./p-DlYSClKt.js'), []],
    dots: [async () => await import('./p-BkjXESS5.js'), []],
    download: [async () => await import('./p-C--GJR6B.js'), []],
    droplet: [async () => await import('./p-BrBRyuWs.js'), []],
    'external-link': [async () => await import('./p-BJSYzJ7z.js'), []],
    'eye-off': [async () => await import('./p-lY9-HeNg.js'), []],
    'eye-x': [async () => await import('./p-8kUBDPoM.js'), []],
    eye: [async () => await import('./p-DBHWCpYt.js'), []],
    'file-text': [async () => await import('./p-Bgc8DL9E.js'), []],
    'file-upload': [async () => await import('./p-rarWnr2t.js'), []],
    filter: [async () => await import('./p-udkuniuK.js'), []],
    flask: [async () => await import('./p-CuZBrwlG.js'), []],
    forms: [async () => await import('./p-BzsFbaRU.js'), []],
    github: [async () => await import('./p-BNcc1-EA.js'), []],
    'grip-vertical': [async () => await import('./p-UgQfin0l.js'), []],
    globe: [async () => await import('./p-pJ2AlSfv.js'), []],
    heading: [async () => await import('./p-CtBsaSSC.js'), []],
    headphones: [async () => await import('./p-3Wlqx7rA.js'), []],
    'heart-handshake': [async () => await import('./p-CE7IBcA4.js'), []],
    'help-filled': [async () => await import('./p-CEfF3Za5.js'), []],
    help: [async () => await import('./p-CL1WP9Nv.js'), []],
    home: [async () => await import('./p-CnjQvreI.js'), []],
    'info-circle': [async () => await import('./p-BvhOF6zo.js'), []],
    'input-search': [async () => await import('./p-n2Vgt_b5.js'), []],
    key: [async () => await import('./p-C4FBkiFR.js'), []],
    'layout-grid': [async () => await import('./p-DExWxeWO.js'), []],
    'layout-sidebar-left-collapse': [
        async () => await import('./p-CKn0NSIN.js'),
        [],
    ],
    'layout-sidebar-left-expand': [async () => await import('./p-BvwdnCNY.js'), []],
    link: [async () => await import('./p-BJVnzCGK.js'), []],
    'list-check': [async () => await import('./p-BTgS2OWe.js'), []],
    list: [async () => await import('./p-BhKiF8Iz.js'), []],
    loader: [async () => await import('./p-lwznwebY.js'), []],
    lock: [async () => await import('./p-CrgEbQIe.js'), []],
    login: [async () => await import('./p-Cst3NHv8.js'), []],
    'magnet-down': [async () => await import('./p-D149TRWl.js'), ['rotate-180']],
    'magnet-left': [async () => await import('./p-D149TRWl.js'), ['rotate-270']],
    'magnet-right': [async () => await import('./p-D149TRWl.js'), ['rotate-90']],
    'magnet-up': [async () => await import('./p-D149TRWl.js'), []],
    mail: [async () => await import('./p-Be9lIHrS.js'), []],
    minus: [async () => await import('./p-CycZ1n3V.js'), []],
    maximize: [async () => await import('./p-Dna1Kyks.js'), []],
    moon: [async () => await import('./p-DqdHXFnd.js'), []],
    'menu-3': [async () => await import('./p-BqKf1goV.js'), []],
    movie: [async () => await import('./p-0zF8N38B.js'), []],
    pencil: [async () => await import('./p-B9bruFDM.js'), []],
    phone: [async () => await import('./p-DEYnXUOt.js'), []],
    'photo-off': [async () => await import('./p-BQ9QLflV.js'), []],
    photo: [async () => await import('./p-CKZBWdny.js'), []],
    'picture-in-picture': [async () => await import('./p-CswdIPbP.js'), []],
    pig: [async () => await import('./p-CiPFHDLh.js'), []],
    'pig-off': [async () => await import('./p-DcKFe65y.js'), []],
    'pig-money': [async () => await import('./p-B7IREnFV.js'), []],
    'player-pause': [async () => await import('./p-B3Q3vfdp.js'), []],
    'player-play': [async () => await import('./p-DnQNYpmj.js'), []],
    'player-skip-forward': [async () => await import('./p-DZrDnINw.js'), []],
    'player-stop': [async () => await import('./p-ihk24pVk.js'), []],
    playlist: [async () => await import('./p-CFFKnbBr.js'), []],
    plus: [async () => await import('./p-Ue_LXoA6.js'), []],
    pointer: [async () => await import('./p-CJajmrrv.js'), []],
    puzzle: [async () => await import('./p-IB-w0Ic6.js'), []],
    'question-mark': [async () => await import('./p-CHhv-ESd.js'), []],
    refresh: [async () => await import('./p-1YcrO-Vx.js'), []],
    'report-analytics': [async () => await import('./p-qWngOLlU.js'), []],
    'rewind-backward-10': [async () => await import('./p-DBDbP9s6.js'), []],
    rotate: [async () => await import('./p-SgQTl9Ml.js'), []],
    rss: [async () => await import('./p-Cqqrodnr.js'), []],
    search: [async () => await import('./p-BGFmNr9x.js'), []],
    selector: [async () => await import('./p-86Slk24x.js'), []],
    send: [async () => await import('./p-asH5Y6_n.js'), []],
    settings: [async () => await import('./p-g6xvyFMf.js'), []],
    'share-2': [async () => await import('./p-DHesxzyM.js'), []],
    'share-3': [async () => await import('./p-DJavsrCz.js'), []],
    share: [async () => await import('./p-CzZQnuHv.js'), []],
    shareplay: [async () => await import('./p-CFlyviQ1.js'), []],
    sitemap: [async () => await import('./p-BiR-A_id.js'), []],
    slash: [async () => await import('./p-DntF3rCk.js'), []],
    social: [async () => await import('./p-CJbxOyCy.js'), []],
    'sort-ascending': [async () => await import('./p-DfouJk92.js'), []],
    'sort-descending': [async () => await import('./p-wS5hyM0G.js'), []],
    sparkles: [async () => await import('./p-ujbfeb2N.js'), []],
    'square-key': [async () => await import('./p-B9nY3Ii7.js'), []],
    star: [async () => await import('./p-C7D3bN5S.js'), []],
    sun: [async () => await import('./p-BIdSwgoT.js'), []],
    tags: [async () => await import('./p-IOLb-wCm.js'), []],
    'text-recognition': [async () => await import('./p-DovwdcWB.js'), []],
    'timeline-event-text': [async () => await import('./p-BhUlkJwm.js'), []],
    tool: [async () => await import('./p-5IGYyEi9.js'), []],
    trash: [async () => await import('./p-BpFX99P3.js'), []],
    target: [async () => await import('./p-VvMpOyX6.js'), []],
    'trending-up': [async () => await import('./p-DcL1n5l-.js'), []],
    unlink: [async () => await import('./p-B4Evdw6Y.js'), []],
    upload: [async () => await import('./p-B0arW51j.js'), []],
    'user-search': [async () => await import('./p-CWUVMC4F.js'), []],
    users: [async () => await import('./p-AvdrV07B.js'), []],
    video: [async () => await import('./p-Cw6zLh6t.js'), []],
    'video-plus': [async () => await import('./p-CYQekxiS.js'), []],
    'volume-2': [async () => await import('./p-B4orLjoO.js'), []],
    'volume-3': [async () => await import('./p-DZu8Q2mi.js'), []],
    volume: [async () => await import('./p-BmhjTTXd.js'), []],
    world: [async () => await import('./p-dO-EMI_Q.js'), []],
    x: [async () => await import('./p-BUhSh2iX.js'), []],
    'zoom-out': [async () => await import('./p-Ct6nKGYI.js'), []],
    'zoom-in': [async () => await import('./p-fUR-vEI5.js'), []],
    'zoom-reset': [async () => await import('./p-CSYVy5yl.js'), []],
};

const ICON_NAMES = Object.keys(ICONS);

const iconCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:inline-flex;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;line-height:0!important;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}div{display:block}svg{height:100%;width:100%}svg,svg *{pointer-events:none}:host([size=xs]){height:.5rem;width:.5rem}:host([size=xs][role=button]):before{content:\"\";inset:-10px;position:absolute}:host([size=sm]){height:.75rem;width:.75rem}:host([size=sm][role=button]):before{content:\"\";inset:-8px;position:absolute}:host([size=md]){height:1rem;width:1rem}:host([size=md][role=button]):before{content:\"\";inset:-4px;position:absolute}:host([size=lg]){height:1.25rem;width:1.25rem}:host([size=xl]){height:1.5rem;width:1.5rem}:host([size=\"2xl\"]){height:3rem;width:3rem}:host([role=button]){cursor:pointer;position:relative}:host([role=button])[disabled]{cursor:not-allowed}:host([role=button]):focus{outline:none}.rotate-90{rotate:90deg}.rotate-180{rotate:180deg}.rotate-270{rotate:270deg}";

function isIconName(name) {
    return Boolean(name !== 'NULL' && typeof name !== 'undefined' && name in ICONS);
}
const WuiIcon = /*@__PURE__*/ proxyCustomElement(class WuiIcon extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        /**
         * Icon name
         */
        this.name = 'NULL';
        this.getIconSvg = PromiseUtil.latest(async (name) => {
            if (!isIconName(name)) {
                return {
                    svg: '',
                    classes: [],
                };
            }
            const [importSvg, classes] = ICONS[name];
            const svg = (await importSvg()).default;
            return {
                svg,
                classes,
            };
        });
        /**
         * Size
         */
        this.size = 'md';
        this.classes = [];
        this.svg = '';
    }
    // We lazy load all icon SVGs by watching when the `name` property changes.
    // We then request the icon asynchronously, and once it's loaded, we replace
    // the internal state with the new SVG markup.
    //
    // We use abort controllers to make sure that we only handle the latest SVG request.
    // Any requests made earlier but that took longer to respond, will be ignored.
    async watchName(value) {
        try {
            const { svg, classes } = await this.getIconSvg(value);
            this.svg = svg;
            this.classes = classes;
        }
        catch (err) {
            console.error('icon', err);
            // ignore (abort) errors
        }
    }
    componentWillLoad() {
        // Load the initial icon SVG by triggering the watch function.
        this.watchName(this.name);
    }
    render() {
        if (!isIconName(this.name)) {
            return h(Host, null);
        }
        return (h(Host, null, h("div", { class: this.classes.join(' '), innerHTML: this.svg })));
    }
    static get watchers() { return {
        "name": ["watchName"]
    }; }
    static get style() { return iconCss; }
}, [257, "wui-icon", {
        "name": [513],
        "size": [513],
        "classes": [32],
        "svg": [32]
    }, undefined, {
        "name": ["watchName"]
    }]);
WuiIcon.ICON_NAMES = ICON_NAMES;
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-icon":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiIcon);
            }
            break;
    } });
}
defineCustomElement();

export { WuiIcon as W, defineCustomElement as d };
